class BlogSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
  }

  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    this.form = this.querySelector('form');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    
    if (!this.input || !this.form || !this.predictiveSearchResults) {
      return;
    }

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener('input', debounce(this.onChange.bind(this), 300));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const newSearchTerm = this.getQuery();
    
    if (this.searchTerm !== newSearchTerm) {
      if (!newSearchTerm.startsWith(this.searchTerm)) {
        this.predictiveSearchResults.innerHTML = '';
      }
    }

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length) {
      event.preventDefault();
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();
    
    if (!currentSearchTerm.length) {
      return;
    }

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut(event) {
    if (!this.contains(document.activeElement)) {
      this.close(false);
    }
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(/\s+/g, '-').toLowerCase();
    
    this.open();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    this.abortController.abort();
    this.abortController = new AbortController();

    const params = new URLSearchParams({
      q: searchTerm,
      'resources[type]': 'article',
      'resources[limit]': '10',
      section_id: 'predictive-search'
    });
    
    const searchUrl = `${window.routes.predictive_search_url}?${params.toString()}`;
    
    fetch(searchUrl, {
      signal: this.abortController.signal
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.text();
      })
      .then((text) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(text, 'text/html');
        const section = doc.querySelector('#shopify-section-predictive-search');
        
        if (section) {
          const resultsWrapper = section.querySelector('#predictive-search-results');
          
          if (resultsWrapper) {
            const resultsMarkup = resultsWrapper.innerHTML;
            const hasResults = resultsWrapper.querySelector('.predictive-search__list-item');
            
            if (hasResults) {
              this.cachedResults[queryKey] = resultsMarkup;
              this.renderSearchResults(resultsMarkup);
            } else {
              this.renderNoResults();
            }
          } else {
            const resultsMarkup = section.innerHTML;
            this.cachedResults[queryKey] = resultsMarkup;
            this.renderSearchResults(resultsMarkup);
          }
        } else {
          this.renderNoResults();
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          return;
        }
        this.renderNoResults();
      });
  }

  renderSearchResults(resultsMarkup) {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = resultsMarkup;
    
    const loadingState = tempDiv.querySelector('.predictive-search__loading-state');
    if (loadingState) {
      loadingState.remove();
    }
    
    this.predictiveSearchResults.innerHTML = tempDiv.innerHTML;
    this.setAttribute('results', 'true');
    this.open();
  }

  renderNoResults() {
    const noResultsText = this.dataset.noResultsText || `No articles found for "${this.searchTerm}"`;
    this.predictiveSearchResults.innerHTML = `
      <div class="blog-predictive-search__no-results">
        <p>${noResultsText.replace('[SEARCH_TERM]', this.searchTerm)}</p>
      </div>
    `;
    this.setAttribute('results', 'true');
    this.open();
  }

  open() {
    this.setAttribute('open', 'true');
    if (this.input) {
      this.input.setAttribute('aria-expanded', 'true');
    }
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      if (this.input) {
        this.input.value = '';
      }
      this.removeAttribute('results');
      this.predictiveSearchResults.innerHTML = '';
    }
    
    this.removeAttribute('open');
    if (this.input) {
      this.input.setAttribute('aria-expanded', 'false');
    }
    this.isOpen = false;
  }
}

customElements.define('blog-search', BlogSearch);
